/** Parcel-scale outdoor authoring shared by the town lab and streamed worlds.
 * Coordinates are metres in the caller's X/Z chart; Y is supplied by terrain.
 * Stable plot/feature keys own their random draws. No settlement-wide prop roll
 * or global cap can change an existing plot when another district is streamed. */
import * as pmath from '../pmath.js';
import {rngFromString} from '../rng.js';
export type OutdoorVec=[number,number,number];
export type OutdoorKind='home'|'market'|'workshop'|'store'|'farm'|'civic';
export type OutdoorRegion='meadow'|'hillside'|'river';
export type OutdoorMaterial='stone'|'cap'|'wood'|'iron'|'soil'|'leaf'|'flower'|'linen'|'blue';
export interface OutdoorBounds {x:number;z:number;w:number;d:number;yaw:number}
export interface OutdoorPart {id:string;center:OutdoorVec;size:OutdoorVec;yaw:number;material:OutdoorMaterial;shape?:'cylinder'|'leaf';solid:boolean;floor?:boolean}
export interface OutdoorSpace {
 id:string;plotId:string;kind:'garden'|'gathering'|'workyard'|'delivery'|'terrace';region:OutdoorRegion;
 bounds:OutdoorBounds;parts:OutdoorPart[];
 access?:OutdoorBounds;
 activity?:{id:string;kind:'rest'|'garden'|'repair'|'deliver'|'browse';position:OutdoorVec;approach:OutdoorVec;facing:OutdoorVec};
}
export interface OutdoorPlan {version:1;id:string;spaces:OutdoorSpace[];stats:{plots:number;attempts:number;rejected:number}}
export interface OutdoorPlot {
 id:string;kind:OutdoorKind;tier:number;region:OutdoorRegion;
 /** Polygon in X/Z, with any winding; ownership never extends beyond it. */
 boundary:readonly number[];level:number;
 /** Point and outward direction; keep the existing doorway/route authoritative. */
 front:{x:number;z:number;nx:number;nz:number};
 retaining?:boolean;
 /** Actual terrace transition, distinct from the larger outdoor ownership lot. */
 retainingBoundary?:readonly number[];
}
export interface OutdoorInput {
 id:string;plots:readonly OutdoorPlot[];height:(x:number,z:number)=>number;
 /** Clearance to streets AND reserved movement/activity corridors, metres. */
 clear:(x:number,z:number,radius:number)=>boolean;
 occupied:readonly OutdoorBounds[];
}
/** Per-plot density, never a cap on an entire city. Large tiers add shared courts
 * and compact planting rather than scaling furniture or repeating huge farms. */
export const OUTDOOR_TIER_BUDGET=[2,3,4,5,5,6] as const;
const roll=(key:string)=>rngFromString(key)();
const clamp=(n:number,a:number,b:number)=>Math.max(a,Math.min(b,n));
export function outdoorContains(poly:readonly number[],x:number,z:number){
 let inside=false;
 for(let i=0,j=poly.length-2;i<poly.length;j=i,i+=2){
  const ax=poly[i],az=poly[i+1],bx=poly[j],bz=poly[j+1];
  if((az>z)!==(bz>z)&&x<(bx-ax)*(z-az)/(bz-az)+ax)inside=!inside;
 }
 return inside;
}
export function outdoorPoint(b:OutdoorBounds,x:number,z:number):[number,number]{
 const c=pmath.cos(b.yaw),s=pmath.sin(b.yaw);return [b.x+c*x-s*z,b.z+s*x+c*z];
}
export function outdoorDistance(b:OutdoorBounds,x:number,z:number):number {
 const c=pmath.cos(b.yaw),s=pmath.sin(b.yaw),dx=x-b.x,dz=z-b.z;
 const u=Math.abs(dx*c+dz*s)-b.w/2,v=Math.abs(-dx*s+dz*c)-b.d/2;
 return pmath.hypot(Math.max(0,u),Math.max(0,v))+Math.min(0,Math.max(u,v));
}
function overlaps(a:OutdoorBounds,b:OutdoorBounds,margin:number){
 const ca=pmath.cos(a.yaw),sa=pmath.sin(a.yaw),cb=pmath.cos(b.yaw),sb=pmath.sin(b.yaw);
 for(const [x,z] of [[ca,sa],[-sa,ca],[cb,sb],[-sb,cb]]){
  const ra=Math.abs(x*ca+z*sa)*a.w/2+Math.abs(-x*sa+z*ca)*a.d/2;
  const rb=Math.abs(x*cb+z*sb)*b.w/2+Math.abs(-x*sb+z*cb)*b.d/2;
  if(Math.abs((b.x-a.x)*x+(b.z-a.z)*z)>ra+rb+margin)return false;
 }
 return true;
}
/** The placement broad phase stays local even for a 600-parcel metropolis. */
class BoundsIndex {
 private cells=new Map<string,{bounds:OutdoorBounds;join?:string}[]>();
 private keys(b:OutdoorBounds,margin=0){
  const r=pmath.hypot(b.w,b.d)/2+margin,keys:string[]=[];
  for(let x=Math.floor((b.x-r)/16);x<=Math.floor((b.x+r)/16);x++)for(let z=Math.floor((b.z-r)/16);z<=Math.floor((b.z+r)/16);z++)keys.push(`${x},${z}`);
  return keys;
 }
 add(b:OutdoorBounds,join?:string){for(const key of this.keys(b)){const list=this.cells.get(key)??[];list.push({bounds:b,join});this.cells.set(key,list);}}
 blocked(b:OutdoorBounds,margin=.45,join?:string){return this.keys(b,margin).some(key=>(this.cells.get(key)??[]).some(other=>(!join||other.join!==join)&&overlaps(b,other.bounds,margin)));}
}
function footprintSamples(b:OutdoorBounds){
 const points:[number,number][]=[];
 const nx=Math.max(2,Math.ceil(b.w/.7)),nz=Math.max(2,Math.ceil(b.d/.7));
 for(let i=0;i<=nx;i++)for(let j=0;j<=nz;j++)points.push(outdoorPoint(b,(i/nx-.5)*b.w,(j/nz-.5)*b.d));
 return points;
}
/** A small branching plant, with low foliage and separate flower heads. Keeping
 * every cluster inside its bed avoids grass-like spill into walking space. */
function plantedCluster(parts:OutdoorPart[],bed:OutdoorBounds,id:string,x:number,y:number,z:number,height:number,flower:boolean){
 const put=(name:string,dx:number,dy:number,dz:number,w:number,h:number,d:number,material:OutdoorMaterial,shape?:OutdoorPart['shape'],yaw=0)=>{
  const p=outdoorPoint(bed,x+dx,z+dz);parts.push({id:`${id}:${name}`,center:[p[0],y+dy,p[1]],size:[w,h,d],yaw:bed.yaw+yaw,material,shape,solid:false});
 };
 const angle=roll(`${id}:angle`)*Math.PI*2;
 put('stem',0,height/2,0,.018,height,.018,'leaf');
 for(let leaf=0;leaf<4;leaf++){
  const a=angle+leaf*Math.PI/2,reach=.06,dy=.04+leaf*height*.1;
  put(`leaf:${leaf}`,pmath.cos(a)*reach,dy,pmath.sin(a)*reach,.19,.09,.09,'leaf','leaf',a);
 }
 if(flower){
  for(let head=0;head<3;head++){
   const a=angle+head*Math.PI*2/3,dx=pmath.cos(a)*.055,dz=pmath.sin(a)*.055;
   put(`bloom:${head}`,dx,height-head*.035,dz,.065,.035,.065,'flower','leaf');
  }
 }else put('shoot',0,height*.65,0,.09,height*.6,.09,'leaf','leaf');
}
export function buildOutdoors(input:OutdoorInput):OutdoorPlan {
 const spaces:OutdoorSpace[]=[],index=new BoundsIndex();input.occupied.forEach(b=>index.add(b));
 const stats={plots:input.plots.length,attempts:0,rejected:0};
 const fits=(plot:OutdoorPlot,b:OutdoorBounds,margin=.45,join?:string)=>{
  stats.attempts++;
  if(index.blocked(b,margin,join)||!footprintSamples(b).every(([x,z])=>outdoorContains(plot.boundary,x,z)&&input.clear(x,z,.35))){stats.rejected++;return false;}
  return true;
 };
 const add=(space:OutdoorSpace)=>{spaces.push(space);index.add(space.bounds,space.kind==='terrace'?`${space.plotId}:retaining`:undefined);};
 for(const plot of [...input.plots].sort((a,b)=>a.id<b.id?-1:a.id>b.id?1:0)){
  const key=`${input.id}:outdoors-v1:${plot.id}`,tier=clamp(Math.floor(plot.tier),0,5),budget=OUTDOOR_TIER_BUDGET[tier];
  // Seat a retaining face on the finished grade. Skip entrances/roads instead
  // of closing the plot with a wall; tall cuts retain the existing earth slope.
  if(plot.retaining!==false){
   let segments=0;
   const boundary=plot.retainingBoundary??plot.boundary;
   for(let e=0;e<boundary.length;e+=2){
    const next=(e+2)%boundary.length,ax=boundary[e],az=boundary[e+1],dx=boundary[next]-ax,dz=boundary[next+1]-az,len=pmath.hypot(dx,dz),n=Math.ceil(len/2.2);
    if(len<.1)continue;
    for(let i=0;i<n&&segments<32;i++){
     const t=(i+.5)/n,b:OutdoorBounds={x:ax+dx*t,z:az+dz*t,w:len/n+.025,d:.48,yaw:pmath.atan2(dz,dx)};
     // Slight inset keeps the entire masonry footprint on its owning parcel.
     const left=[-dz/len,dx/len],sign=outdoorContains(boundary,b.x+left[0]*.3,b.z+left[1]*.3)?1:-1;
     b.x+=left[0]*sign*.38;b.z+=left[1]*sign*.38;
     const inner=input.height(b.x+left[0]*sign*.75,b.z+left[1]*sign*.75),outer=input.height(b.x-left[0]*sign*.75,b.z-left[1]*sign*.75);
     const heights=footprintSamples(b).map(([x,z])=>input.height(x,z)),bottom=Math.min(...heights)-.22,top=Math.min(plot.level+.08,inner+.08);
     // A wall must actually support earth on its back. Merely comparing a
     // remote parcel level to low ground created freestanding walls in grass.
     if(inner-outer<.3||top-Math.max(...heights)<.12||top-bottom>2.5||!fits(plot,b,.1,`${plot.id}:retaining`))continue;
     const id=`${plot.id}:retaining:${e/2}:${i}`,parts:OutdoorPart[]=[];
     parts.push({id:`${id}:body`,center:[b.x,(top+bottom)/2,b.z],size:[b.w,top-bottom,b.d],yaw:b.yaw,material:'stone',solid:true});
     parts.push({id:`${id}:cap`,center:[b.x,top+.06,b.z],size:[b.w+.02,.12,b.d+.1],yaw:b.yaw,material:'cap',solid:true});
     // Shallow masonry courses give the exposed face scale without thousands
     // of individual colliders. Their depth stays inside the cap footprint.
     for(let y=bottom+.38;y<top-.12;y+=.38)parts.push({id:`${id}:course:${parts.length}`,center:[b.x,y,b.z],size:[b.w,.025,b.d+.025],yaw:b.yaw,material:'cap',solid:false});
     add({id,plotId:plot.id,kind:'terrace',region:plot.region,bounds:{...b,d:.6},parts});segments++;
    }
   }
  }
  const xs=plot.boundary.filter((_,i)=>i%2===0),zs=plot.boundary.filter((_,i)=>i%2===1),minX=Math.min(...xs),maxX=Math.max(...xs),minZ=Math.min(...zs),maxZ=Math.max(...zs);
  const kinds=plot.kind==='home'?['garden','gathering']:plot.kind==='market'?['gathering','delivery','garden']:plot.kind==='workshop'?['workyard','delivery']:plot.kind==='store'?['delivery','workyard']:plot.kind==='farm'?['garden','workyard']:['gathering','garden'];
  let count=0;const authoredKinds=new Set<OutdoorSpace['kind']>();
  // Fixed keyed candidates: rejection consumes no other plot's random stream.
  for(let attempt=0;attempt<40&&count<budget;attempt++){
   const id=`${plot.id}:outdoor:${attempt}`,kind=kinds[attempt%kinds.length] as OutdoorSpace['kind'];
   // One useful court per purpose. Density adds planted edges instead of
   // scattering identical worktables or benches around the same building.
   if(authoredKinds.has(kind))continue;
   const scale=.85+roll(`${key}:${attempt}:scale`)*.3;
   const w=(kind==='garden'?(tier>=3?2:3):kind==='gathering'?3.6:2.7)*scale,d=(kind==='gathering'?2.6:2)*scale;
   const b:OutdoorBounds={x:minX+(maxX-minX)*roll(`${key}:${attempt}:x`),z:minZ+(maxZ-minZ)*roll(`${key}:${attempt}:z`),w,d,yaw:pmath.atan2(-plot.front.nx,plot.front.nz)};
   if(!fits(plot,b))continue;
   const heights=footprintSamples(b).map(([x,z])=>input.height(x,z)),low=Math.min(...heights),high=Math.max(...heights);
   if(high-low>(plot.region==='hillside'?.75:.5)){stats.rejected++;continue;}
   // Each station opens into a reserved approach on the front side of its pad.
   const y=high+.065,[px,pz]=outdoorPoint(b,0,d/2-.35),[ax,az]=outdoorPoint(b,0,d/2+1.5),entryY=input.height(ax,az),rise=y-entryY;
   const approach:OutdoorBounds={...b,x:(px+ax)/2,z:(pz+az)/2,w:1.2,d:1.85};
   if(!footprintSamples(approach).every(([x,z])=>input.clear(x,z,.4)&&outdoorContains(plot.boundary,x,z))||index.blocked(approach,.2)||rise>.64||rise<-.16){stats.rejected++;continue;}
   const parts:OutdoorPart[]=[];
   const part=(name:string,x:number,yy:number,z:number,sx:number,sy:number,sz:number,material:OutdoorMaterial,solid=true,shape?:OutdoorPart['shape'])=>{
    const p=outdoorPoint(b,x,z);parts.push({id:`${id}:${name}`,center:[p[0],yy,p[1]],size:[sx,sy,sz],yaw:b.yaw,material,solid,shape});
   };
   part('foundation',0,(low-.16+y)/2,0,w,y-low+.16,d,'stone');parts[0].floor=true;
   if(rise>.16){
    const steps=Math.ceil(rise/.16),run=1.28/steps;
    for(let step=0;step<steps;step++){
     const z=d/2+(steps-step-.5)*run,top=entryY+(step+1)*rise/steps,p=outdoorPoint(b,0,z),bottom=Math.min(input.height(p[0],p[1])-.18,entryY-.12);
     part(`step:${step}`,0,(top+bottom)/2,z,1.1,top-bottom,run+.02,'cap');parts.at(-1)!.floor=true;
    }
   }
   // A fitted limestone border frames the useful area and hides the slab edge.
   part('border-left',-w/2+.06,y+.025,0,.12,.05,d,'cap',false);
   part('border-right',w/2-.06,y+.025,0,.12,.05,d,'cap',false);
   const activity:NonNullable<OutdoorSpace['activity']>={id:`${id}:activity`,kind:kind==='garden'?'garden':kind==='workyard'?'repair':kind==='delivery'?'deliver':plot.kind==='market'?'browse':'rest',position:[px,y,pz],approach:[ax,input.height(ax,az),az],facing:[-plot.front.nx,0,-plot.front.nz]};
   if(kind==='garden'){
    const bedW=w-.38,bedD=d*.48,z=-d*.18;
    part('soil',0,y+.16,z,bedW,.28,bedD,'soil');
    for(const side of [-1,1])part(`bed-end:${side}`,side*bedW/2,y+.2,z,.1,.4,bedD+.1,plot.region==='river'?'wood':'cap');
    for(const side of [-1,1])part(`bed-side:${side}`,0,y+.2,z+side*bedD/2,bedW,.4,.1,plot.region==='river'?'wood':'cap');
    const plants=Math.max(3,Math.floor(bedW/.4));
    for(let i=0;i<plants;i++){
     const x=(i/(plants-1)-.5)*(bedW-.38),h=.2+roll(`${key}:${attempt}:plant:${i}`)*.24;
     plantedCluster(parts,b,`${id}:plant:${i}`,x,y+.3,z+(i%2?1:-1)*bedD*.2,h,plot.kind!=='farm');
    }
   }else if(kind==='gathering'){
    part('seat',0,y+.47,-d*.25,w*.7,.12,.5,'wood');
    for(const side of [-1,1])part(`leg:${side}`,side*w*.26,y+.21,-d*.25,.16,.42,.36,'wood');
    part('back',0,y+.82,-d*.25-.25,w*.7,.42,.09,'wood');
    // Human-scale tables and jars give markets/courts a clear gathering purpose.
    part('table',w*.34,y+.56,d*.06,.45,.08,.45,'wood');part('table-post',w*.34,y+.26,d*.06,.13,.52,.13,'wood');
    part('jar',w*.34,y+.72,d*.06,.2,.24,.2,'blue',false,'cylinder');
   }else{
    part('worktop',0,y+.84,-d*.22,w*.65,.13,.6,'wood');
    for(let plank=0;plank<3;plank++)part(`worktop-plank:${plank}`,0,y+.914,-d*.22+(plank-1)*.2,w*.65,.02,.185,'wood',false);
    for(const side of [-1,1]){
     for(const leg of [-1,1])part(`leg:${side}:${leg}`,side*w*.23,y+.4,-d*.22+leg*.2,.12,.8,.12,'wood');
     part(`trestle:${side}`,side*w*.23,y+.12,-d*.22,.2,.1,.55,'wood');
    }
    part('crate',-w*.33,y+.25,d*.08,.52,.5,.52,'wood');
    for(const side of [-1,1])part(`crate-band:${side}`,-w*.33+side*.18,y+.25,d*.08,.035,.51,.535,'iron',false);
    part('barrel',w*.32,y+.35,-d*.25,.52,.7,.52,'wood',true,'cylinder');
    for(const h of [.13,.55])part(`hoop:${h}`,w*.32,y+h,-d*.25,.55,.065,.55,'iron',false,'cylinder');
    if(kind==='delivery'){
     for(let sack=0;sack<3;sack++)part(`bundle:${sack}`,(sack-1)*.21,y+.98+(sack%2)*.07,-d*.22,.19,.15+(sack%2)*.14,.28,'linen',false);
    }else if(plot.kind==='farm'){
     part('seed-tray',0,y+.95,-d*.22,.5,.065,.35,'wood',false);
     part('tray-soil',0,y+.987,-d*.22,.44,.02,.29,'soil',false);
     for(let plant=0;plant<2;plant++)plantedCluster(parts,b,`${id}:seedling:${plant}`,(plant-.5)*.21,y+1,-d*.22,.16,false);
    }else{
     part('anvil-foot',0,y+.97,-d*.22,.34,.09,.24,'iron',false);
     part('anvil-waist',0,y+1.06,-d*.22,.17,.12,.16,'iron',false);
     part('anvil-face',-.035,y+1.15,-d*.22,.43,.08,.21,'iron',false);
     part('hammer-handle',w*.2,y+.951,-d*.2,.025,.035,.26,'wood',false);
     part('hammer-head',w*.2,y+.97,-d*.2-.1,.15,.08,.07,'iron',false);
    }
   }
   add({id,plotId:plot.id,kind,region:plot.region,bounds:b,parts,activity,access:approach});index.add(approach);authoredKinds.add(kind);count++;
  }
  // Tight legacy lots cannot always fit a working court. Fit small planted
  // edges into their actual setbacks instead of forcing a full-size asset in.
  for(let attempt=0;attempt<32&&count<budget;attempt++){
   const e=(attempt% (plot.boundary.length/2))*2,next=(e+2)%plot.boundary.length,ax=plot.boundary[e],az=plot.boundary[e+1],dx=plot.boundary[next]-ax,dz=plot.boundary[next+1]-az,len=pmath.hypot(dx,dz);
   if(len<1)continue;
   const t=.08+.84*roll(`${key}:planter:${attempt}`),b:OutdoorBounds={x:ax+dx*t,z:az+dz*t,w:.8,d:.5,yaw:pmath.atan2(dz,dx)},nx=-dz/len,nz=dx/len,sign=outdoorContains(plot.boundary,b.x+nx*.3,b.z+nz*.3)?1:-1;
   b.x+=nx*sign*.35;b.z+=nz*sign*.35;
   if(!fits(plot,b,.08))continue;
   const ys=footprintSamples(b).map(([x,z])=>input.height(x,z)),low=Math.min(...ys),high=Math.max(...ys);if(high-low>.18)continue;
   const id=`${plot.id}:planter:${attempt}`,parts:OutdoorPart[]=[
    {id:`${id}:base`,center:[b.x,(low-.12+high+.38)/2,b.z],size:[b.w,high-low+.5,b.d],yaw:b.yaw,material:plot.region==='river'?'wood':'stone',solid:true},
    {id:`${id}:soil`,center:[b.x,high+.38,b.z],size:[b.w-.08,.04,b.d-.08],yaw:b.yaw,material:'soil',solid:false},
   ];
   for(let plant=0;plant<3;plant++)plantedCluster(parts,b,`${id}:plant:${plant}`,(plant-1)*.22,high+.4,plant%2?.05:-.05,.2+roll(`${key}:planter:${attempt}:plant:${plant}`)*.17,true);
   add({id,plotId:plot.id,kind:'garden',region:plot.region,bounds:b,parts});count++;
  }
 }
 return {version:1,id:`${input.id}:outdoors-v1`,spaces,stats};
}
export function outdoorParts(plan:OutdoorPlan){return plan.spaces.flatMap(s=>s.parts);}
const queryIndices=new WeakMap<OutdoorPlan,Map<string,OutdoorSpace[]>>();
function spacesAt(plan:OutdoorPlan,x:number,z:number,radius=0){
 let cells=queryIndices.get(plan);
 if(!cells){cells=new Map();for(const space of plan.spaces)for(const b of [space.bounds,...(space.access?[space.access]:[])]){
  const r=pmath.hypot(b.w,b.d)/2;
  for(let ix=Math.floor((b.x-r)/16);ix<=Math.floor((b.x+r)/16);ix++)for(let iz=Math.floor((b.z-r)/16);iz<=Math.floor((b.z+r)/16);iz++){
   const key=`${ix},${iz}`,list=cells.get(key)??[];list.push(space);cells.set(key,list);
  }
 }queryIndices.set(plan,cells);}
 const result=new Set<OutdoorSpace>();for(let ix=Math.floor((x-radius)/16);ix<=Math.floor((x+radius)/16);ix++)for(let iz=Math.floor((z-radius)/16);iz<=Math.floor((z+radius)/16);iz++)for(const s of cells.get(`${ix},${iz}`)??[])result.add(s);
 return result;
}
export function outdoorBlocked(plan:OutdoorPlan,x:number,z:number,y:number,radius=.3){
 for(const s of spacesAt(plan,x,z,radius))if(outdoorDistance(s.bounds,x,z)<=radius&&s.parts.some(p=>p.solid&&!p.floor&&p.center[1]+p.size[1]/2>y+.1&&p.center[1]-p.size[1]/2<y+1.7&&outdoorDistance({x:p.center[0],z:p.center[2],w:p.size[0],d:p.size[2],yaw:p.yaw},x,z)<radius))return true;
 return false;
}
export function outdoorFloor(plan:OutdoorPlan,x:number,z:number,base:number){
 for(const s of spacesAt(plan,x,z))if(outdoorDistance(s.bounds,x,z)<=0||s.access&&outdoorDistance(s.access,x,z)<=0)for(const p of s.parts)if(p.floor&&outdoorDistance({x:p.center[0],z:p.center[2],w:p.size[0],d:p.size[2],yaw:p.yaw},x,z)<=0)base=Math.max(base,p.center[1]+p.size[1]/2);
 return base;
}
